import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
}

function formatCompact(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return formatCurrency(value);
}

export default function MonthlyOutlookTable({ monthlyOutlook, selectedMonth, onSelectMonth }) {
  if (!monthlyOutlook || monthlyOutlook.length === 0) {
    return (
      <Card className="mb-4 sm:mb-6">
        <CardContent className="py-8 text-center text-muted-foreground">
          No outlook data available
        </CardContent>
      </Card>
    );
  }

  const totals = monthlyOutlook.reduce((acc, m) => ({
    expected: acc.expected + (m.expected || 0),
    collected: acc.collected + (m.collected || 0),
    outstanding: acc.outstanding + (m.outstanding || 0),
    count: acc.count + (m.count || 0),
  }), { expected: 0, collected: 0, outstanding: 0, count: 0 });

  return (
    <Card className="mb-4 sm:mb-6">
      <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
        <CardTitle className="text-sm sm:text-base font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>
          Monthly Outlook
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 px-0 sm:px-6">
        {/* Mobile: Horizontal scroll cards */}
        <div className="block sm:hidden">
          <div className="flex gap-2 overflow-x-auto pb-2 px-3 snap-x snap-mandatory">
            {monthlyOutlook.map((m) => (
              <div 
                key={m.month_key}
                onClick={() => onSelectMonth(m.month_key === selectedMonth ? null : m.month_key)}
                className={`flex-shrink-0 w-[140px] p-3 rounded-lg border cursor-pointer snap-start transition-colors ${
                  selectedMonth === m.month_key 
                    ? "bg-primary/10 border-primary" 
                    : "bg-secondary/30 border-border hover:bg-secondary/50"
                }`}
              >
                <p className="font-semibold text-sm mb-2">{m.month}</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Exp:</span>
                    <span className="font-medium">{formatCompact(m.expected)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Out:</span>
                    <span className="font-medium text-orange-600">{formatCompact(m.outstanding)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Items:</span>
                    <span>{m.count}</span>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex-shrink-0 w-[140px] p-3 rounded-lg border bg-secondary/50 snap-start" data-testid="outlook-totals-card">
              <p className="font-bold text-sm mb-2">Total</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Exp:</span>
                  <span className="font-semibold">{formatCompact(totals.expected)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Out:</span>
                  <span className="font-semibold text-orange-600">{formatCompact(totals.outstanding)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Items:</span>
                  <span className="font-semibold">{totals.count}</span>
                </div>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2 px-3">
            ← Scroll & tap to filter →
          </p>
        </div>

        {/* Desktop: Table view */}
        <div className="hidden sm:block border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30">
                <TableHead className="font-medium">Month</TableHead>
                <TableHead className="font-medium text-right">Expected</TableHead>
                <TableHead className="font-medium text-right">Collected</TableHead>
                <TableHead className="font-medium text-right">Outstanding</TableHead>
                <TableHead className="font-medium text-right"># Items</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monthlyOutlook.map((m) => (
                <TableRow 
                  key={m.month_key}
                  className={`cursor-pointer hover:bg-secondary/40 transition-colors ${
                    selectedMonth === m.month_key ? "bg-primary/10" : ""
                  }`}
                  onClick={() => onSelectMonth(m.month_key === selectedMonth ? null : m.month_key)}
                >
                  <TableCell className="font-medium">{m.month}</TableCell>
                  <TableCell className="text-right">{formatCurrency(m.expected)}</TableCell>
                  <TableCell className="text-right text-green-600">{formatCurrency(m.collected)}</TableCell>
                  <TableCell className="text-right text-orange-600 font-medium">
                    {formatCurrency(m.outstanding)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{m.count}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-secondary/50 font-semibold border-t-2" data-testid="outlook-totals-row">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{formatCurrency(totals.expected)}</TableCell>
                <TableCell className="text-right text-green-600">{formatCurrency(totals.collected)}</TableCell>
                <TableCell className="text-right text-orange-600">{formatCurrency(totals.outstanding)}</TableCell>
                <TableCell className="text-right text-muted-foreground">{totals.count}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <p className="hidden sm:block text-xs text-muted-foreground mt-2">
          Click a row to filter the table below
        </p>
      </CardContent>
    </Card>
  );
}
